import { useEffect, useRef, useState } from "react";
import { Car, Users, Trophy, Crown, Medal, Clock, Flag, ChevronDown, Check } from "lucide-react";
import apiClient from "@/lib/apiClient";
import { PageHeader, InlineLoader, EmptyState } from "@/components/common";

const rankColor = (rank) => (rank === 1 ? "#ffb020" : rank === 2 ? "#cbd5e1" : rank === 3 ? "#f59e0b" : "#64748b");

function ClubDropdown({ clubs, active, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative mb-6 w-full sm:w-96" ref={ref} data-testid="club-dropdown">
      <button onClick={() => setOpen((o) => !o)} data-testid="club-dropdown-trigger"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all"
        style={{ background: "linear-gradient(180deg,rgba(0,229,255,0.10),rgba(8,11,22,0.75))", border: `1px solid ${open ? "rgba(0,229,255,0.6)" : "rgba(120,150,220,0.18)"}` }}>
        <span className="inline-flex items-center gap-2.5 min-w-0">
          <span className="w-7 h-7 rounded-lg grid place-items-center shrink-0" style={{ background: "rgba(0,229,255,0.14)", border: "1px solid rgba(0,229,255,0.4)" }}>
            <Car className="w-3.5 h-3.5" style={{ color: "#00e5ff" }} />
          </span>
          <span className="flex flex-col items-start min-w-0">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 leading-none">Car Club</span>
            <span className="text-white font-display font-bold truncate max-w-full leading-tight">{active ? active.name : "Select a club"}</span>
          </span>
        </span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full rounded-xl overflow-hidden shadow-2xl"
          style={{ background: "rgba(8,11,22,0.98)", border: "1px solid rgba(0,229,255,0.3)", backdropFilter: "blur(12px)" }}>
          <div className="max-h-72 overflow-y-auto py-1">
            {clubs.map((c) => {
              const on = active?.id === c.id;
              return (
                <button key={c.id} onClick={() => { onSelect(c); setOpen(false); }} data-testid={`club-opt-${c.id}`}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors hover:bg-white/5"
                  style={on ? { background: "rgba(0,229,255,0.10)" } : {}}>
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <Car className="w-3.5 h-3.5 shrink-0" style={{ color: on ? "#00e5ff" : "#64748b" }} />
                    <span className={`truncate ${on ? "text-white font-semibold" : "text-slate-300"}`}>{c.name}</span>
                    <span className="text-xs text-slate-500 shrink-0">· {c.fan_count}</span>
                  </span>
                  {on && <Check className="w-4 h-4 shrink-0" style={{ color: "#00e5ff" }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CarClubs() {
  const [clubs, setClubs] = useState([]);
  const [active, setActive] = useState(null);
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingBoard, setLoadingBoard] = useState(false);

  useEffect(() => {
    apiClient.get("/car-clubs").then(({ data }) => {
      setClubs(data);
      if (data.length) selectClub(data[0]);
    }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectClub = (c) => {
    setActive(c);
    setLoadingBoard(true);
    apiClient.get(`/car-clubs/${c.id}/leaderboard`).then(({ data }) => setBoard(data.leaderboard || [])).catch(() => setBoard([])).finally(() => setLoadingBoard(false));
  };

  if (loading) return <InlineLoader />;

  return (
    <div>
      <PageHeader title="Car Clubs" subtitle="Fan clubs for the cars our drivers love — climb the ranks by driving your favorite" icon={Car} />
      {clubs.length === 0 ? (
        <EmptyState icon={Car} title="No car clubs yet" message="Once drivers rack up laps, clubs will appear here." />
      ) : (
        <>
          <ClubDropdown clubs={clubs} active={active} onSelect={selectClub} />

          {active && (
            <div className="glass rounded-2xl overflow-hidden" data-testid="club-leaderboard">
              <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between" style={{ background: "rgba(0,229,255,0.05)" }}>
                <div>
                  <p className="text-white font-display font-bold text-lg">{active.name}</p>
                  <p className="text-slate-500 text-xs flex items-center gap-2">
                    <Users className="w-3 h-3" />{active.fan_count} fans · ranked by {active.rank_metric === "hours" ? "hours driven" : "laps driven"}
                  </p>
                </div>
                <Trophy className="w-6 h-6" style={{ color: "#ffb020" }} />
              </div>
              {loadingBoard ? <InlineLoader /> : board.length === 0 ? (
                <p className="text-slate-500 text-sm p-6 text-center">No ranked fans yet.</p>
              ) : (
                <div>
                  {board.map((r) => (
                    <div key={r.user_id} className="flex items-center gap-3 px-5 py-3 border-b border-white/5 last:border-0" data-testid={`club-rank-${r.rank}`}>
                      <span className="w-8 text-center font-display font-bold" style={{ color: rankColor(r.rank) }}>
                        {r.rank === 1 ? <Crown className="w-4 h-4 inline" /> : r.rank <= 3 ? <Medal className="w-4 h-4 inline" /> : `#${r.rank}`}
                      </span>
                      <span className="flex-1 text-white font-semibold truncate">{r.name}</span>
                      <span className="text-slate-400 text-sm inline-flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" />{r.laps} laps</span>
                      <span className="text-slate-400 text-sm inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{r.hours}h</span>
                      <span className="font-display font-bold w-16 text-right" style={{ color: "#00e5ff" }}>
                        {active.rank_metric === "hours" ? `${r.hours}h` : `${r.laps}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
